<template>
  <div class="w-full max-h-screen bg-background flex flex-col relative">
    <page-header title="Downloads & Bulletins" backTo="internal">
      <button
          @click="showingUploadDocumentPanel = true"
          class="flex items-center bg-green-200 border hover:bg-green-300 border-green-400 hover:border-green-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-2 py-1"
      >
        <svg
            class="stroke-current h-5 w-5 mr-2"
            fill="none"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 24 24"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        <span>Upload Document</span>
      </button>
    </page-header>

    <div class="p-6 bg-background flex-1 overflow-y-scroll">
      <div class="w-full bg-white p-5 rounded shadow-md relative">
        <div class="w-full flex items-center justify-between">
          <div class="flex -mx-0 mr-2">
            <TabHeaders
                :tabs="tabs"
                :selectedTab="currentTab"
                @tab-selected="onTabSelected"
            />
          </div>

          <div class="flex flex-col flex-1">
            <div class="flex justify-end">
              <div class="flex items-center mr-4">
                <label class="mr-2">From Date:</label>
                <Calendar
                    class="w-full lg:w-auto"
                    v-model="fromDateInput"
                    dateFormat="dd/mm/yy"
                    :showIcon="true"
                />
              </div>

              <div class="flex items-center mr-4">
                <label class="mr-2">To Date:</label>
                <Calendar
                    class="w-full lg:w-auto"
                    v-model="toDateInput"
                    dateFormat="dd/mm/yy"
                    :showIcon="true"
                />
              </div>

              <div class="flex items-center mr-4">
                <label class="mr-2">Category:</label>
                <select
                    class="bge-input bge-select h-full"
                    v-model="categoryInput"
                    @change="triggerSearch"
                >
                  <option value="">All</option>
                  <optgroup label="Categories">
                    <option>Brochures</option>
                    <option>Certifications</option>
                    <option>Help &amp; Training</option>
                    <option>Miscellaneous</option>
                    <option>Product Manual</option>
                    <option>Sample Plans</option>
                    <option>Schematics</option>
                    <option>Software</option>
                    <option>Specifications</option>
                  </optgroup>
                </select>
              </div>

              <div class="flex items-center mr-4">
                <label class="mr-2">Visibility:</label>
                <select
                    class="bge-input bge-select h-full"
                    v-model="visibilityInput"
                    @change="triggerSearch"
                >
                  <option value="">All</option>
                  <optgroup label="Visibility">
                    <option>Internal</option>
                    <option>Agent</option>
                    <option>External</option>
                    <option>Distributor</option>
                    <option>Public</option>
                  </optgroup>
                </select>
              </div>

<!--              <div class="flex items-center mr-4" v-if="currentTab === 'downloads'">-->
<!--                <label class="mr-2">Access:</label>-->
<!--                <select-->
<!--                    class="bge-input bge-select h-full"-->
<!--                    v-model="accessLevelInput"-->
<!--                    @change="triggerSearch"-->
<!--                >-->
<!--                  <option value="">All</option>-->
<!--                  <optgroup label="Access Level">-->
<!--                    <option>Level 1</option>-->
<!--                    <option>Level 2</option>-->
<!--                  </optgroup>-->
<!--                </select>-->
<!--              </div>-->

              <input
                  type="text"
                  class="ml-6 w-full lg:w-auto rounded-l bge-input bge-input-spacing"
                  placeholder="Search"
                  v-model="searchTextInput"
                  @keydown.enter="triggerSearch"
              />
              <button
                  @click="triggerSearch"
                  class="rounded-r bg-blue-200 hover:bg-blue-300 border border-blue-400 hover:border-blue-500 p-2 hover:shadow active:shadow-inner transition-colors duration-300 ease-in-out"
              >
                <Icon iconType="search" />
              </button>
            </div>
          </div>
        </div>

        <TabContent :currentTab="currentTab">
          <template v-slot:downloads>
            <DataTableComponent
                :columns="downloadsColumns"
                @row-click="onRowClick"
                :key="currentTab"
            >
              <template v-slot:doc-date-slot="{ rowData }">
                {{ formatTimestamp(rowData.doc_date || rowData.document_date) }}
              </template>

              <template v-slot:created-at-slot="{ rowData }">
                {{ formatTimestamp(rowData.created_at) }}
              </template>

              <template v-slot:action-slot="{ rowData }">
                <div class="flex justify-end space-x-2">
                  <AppButton
                      v-if="rowData.content_type === 'application/pdf'"
                      color="orange"
                      label=""
                      :icon="true"
                      @click.native.stop="viewDoc(rowData)"
                  >
                    <template v-slot:icon>
                      <Icon iconType="search" />
                    </template>
                  </AppButton>

                  <AppButton
                      color="green"
                      label=""
                      :icon="true"
                      @click.native.stop="downloadDoc(rowData)"
                  >
                    <template v-slot:icon>
                      <Icon iconType="download" />
                    </template>
                  </AppButton>

                  <AppButton
                      v-if="canEditDelete"
                      color="blue"
                      label=""
                      :icon="true"
                      @click.native.stop="editDoc(rowData)"
                  >
                    <template v-slot:icon>
                      <Icon iconType="edit" />
                    </template>
                  </AppButton>

                  <AppButton
                      v-if="canEditDelete"
                      color="red"
                      label=""
                      :icon="true"
                      @click.native.stop="deleteDoc(rowData)"
                  >
                    <template v-slot:icon>
                      <Icon iconType="delete" />
                    </template>
                  </AppButton>
                </div>
              </template>

            </DataTableComponent>
          </template>

          <template v-slot:bulletins>
            <DataTableComponent
                :columns="bulletinsColumns"
                @row-click="onRowClick"
                :key="currentTab"
            >
              <template v-slot:doc-date-slot="{ rowData }">
                {{ formatTimestamp(rowData.doc_date || rowData.document_date) }}
              </template>

              <template v-slot:created-at-slot="{ rowData }">
                {{ formatTimestamp(rowData.created_at) }}
              </template>

              <template v-slot:action-slot="{ rowData }">
                <div class="flex justify-end space-x-2">
                  <AppButton
                      v-if="rowData.content_type === 'application/pdf'"
                      color="orange"
                      label=""
                      :icon="true"
                      @click.native.stop="viewDoc(rowData)"
                  >
                    <template v-slot:icon>
                      <Icon iconType="search" />
                    </template>
                  </AppButton>

                  <AppButton
                      color="green"
                      label=""
                      :icon="true"
                      @click.native.stop="downloadDoc(rowData)"
                  >
                    <template v-slot:icon>
                      <Icon iconType="download" />
                    </template>
                  </AppButton>

                  <AppButton
                      v-if="canEditDelete"
                      color="blue"
                      label=""
                      :icon="true"
                      @click.native.stop="editDoc(rowData)"
                  >
                    <template v-slot:icon>
                      <Icon iconType="edit" />
                    </template>
                  </AppButton>

                  <AppButton
                      v-if="canEditDelete"
                      color="red"
                      label=""
                      :icon="true"
                      @click.native.stop="deleteDoc(rowData)"
                  >
                    <template v-slot:icon>
                      <Icon iconType="delete" />
                    </template>
                  </AppButton>
                </div>
              </template>

            </DataTableComponent>
          </template>

          <template v-slot:all>
            <DataTableComponent
                :columns="allColumns"
                @row-click="onRowClick"
                :key="currentTab"
            >
              <template v-slot:doc-date-slot="{ rowData }">
                {{ formatTimestamp(rowData.doc_date || rowData.document_date) }}
              </template>

              <template v-slot:created-at-slot="{ rowData }">
                {{ formatTimestamp(rowData.created_at) }}
              </template>

              <template v-slot:action-slot="{ rowData }">
                <div class="flex justify-end space-x-2">
                  <AppButton
                      v-if="rowData.content_type === 'application/pdf'"
                      color="orange"
                      label=""
                      :icon="true"
                      @click.native.stop="viewDoc(rowData)"
                  >
                    <template v-slot:icon>
                      <Icon iconType="search" />
                    </template>
                  </AppButton>

                  <AppButton
                      color="green"
                      label=""
                      :icon="true"
                      @click.native.stop="downloadDoc(rowData)"
                  >
                    <template v-slot:icon>
                      <Icon iconType="download" />
                    </template>
                  </AppButton>

                  <AppButton
                      v-if="canEditDelete"
                      color="blue"
                      label=""
                      :icon="true"
                      @click.native.stop="editDoc(rowData)"
                  >
                    <template v-slot:icon>
                      <Icon iconType="edit" />
                    </template>
                  </AppButton>

                  <AppButton
                      v-if="canEditDelete"
                      color="red"
                      label=""
                      :icon="true"
                      @click.native.stop="deleteDoc(rowData)"
                  >
                    <template v-slot:icon>
                      <Icon iconType="delete" />
                    </template>
                  </AppButton>
                </div>
              </template>
            </DataTableComponent>
          </template>
        </TabContent>
      </div>
    </div>

    <portal to="overlay-outlet">
      <Panel
          :showing="showingUploadDocumentPanel"
          @close="closePanel"
          :title="editingDoc.document_id ? 'Edit Document' : 'Upload Document'"
      >
        <UploadPublishedDoc :document="editingDoc" @complete="onComplete" />
      </Panel>
    </portal>

    <portal to="overlay-outlet">
      <ConfirmAlert
          :showingConfirm="confirmAlert.showing"
          :message="confirmAlert.message"
          @complete="confirmAlert.resultFunction"
      />
    </portal>

    <portal to="overlay-outlet">
      <ViewDocument
          :showing="viewingDocument.panelOpen"
          :document="viewingDocument.doc"
          @close="viewingDocument.panelOpen = false"
      />
    </portal>

  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import PageHeader from "@/components/PageHeader.vue";
import Panel from "@/components/Panel.vue";
import TabHeaders from "@/components/TabHeaders.vue";
import TabContent from "@/components/TabContent.vue";
import Icon from "@/components/Icon.vue";
import AppButton from "@/components/Button.vue";
import Calendar from "primevue/calendar";
import ConfirmAlert from "@/components/ConfirmAlert.vue";
import UploadPublishedDoc from "@/components/Documents/UploadPublishedDoc.vue";
import DataTableComponent from "@/views/Documents/Components/DataTableComponent.vue";
import ViewDocument from "@/components/ViewDocument.vue";

export default {
  components: {
    PageHeader,
    Panel,
    TabHeaders,
    TabContent,
    Icon,
    AppButton,
    Calendar,
    ConfirmAlert,
    UploadPublishedDoc,
    DataTableComponent,
    ViewDocument,
  },

  data() {
    return {
      tabs: [
        { id: "all", label: "All" },
        { id: "downloads", label: "Downloads" },
        { id: "bulletins", label: "Bulletins" },
      ],

      showingUploadDocumentPanel: false,
      editingDoc: {
        visibility: "Internal",
      },

      confirmAlert: {
        resultFunction: "",
        showing: false,
        message: "",
        data: "",
      },
      viewingDocument: {
        doc: {},
        panelOpen: false,
      },
    };
  },

  computed: {
    ...mapState("documents", [
      "currentTab",
      "searchText",
      "fromDate",
      "toDate",
      "category",
      "visibility",
      "accessLevel",
    ]),

    ...mapState("documents", {
      currentTabState: (state) => state.tabs[state.currentTab],
    }),

    canEditDelete: function () {
      return this.$store.state.userRoles.includes("admin") ? true : false;
    },

    searchTextInput: {
      get() {
        return this.searchText;
      },
      set(value) {
        this.$store.commit("documents/UPDATE_SEARCH_TEXT", value);
      },
    },

    fromDateInput: {
      get() {
        const d = this.fromDate;
        return d ? new Date(d) : null;
      },
      set(value) {
        this.$store.commit("documents/UPDATE_FROM_DATE", value);
      },
    },

    toDateInput: {
      get() {
        const d = this.toDate;
        return d ? new Date(d) : null;
      },
      set(value) {
        this.$store.commit("documents/UPDATE_TO_DATE", value);
      },
    },

    categoryInput: {
      get() {
        return this.category;
      },
      set(value) {
        this.$store.commit("documents/UPDATE_CATEGORY", value);
      },
    },

    visibilityInput: {
      get() {
        return this.visibility;
      },
      set(value) {
        this.$store.commit("documents/UPDATE_VISIBILITY", value);
      },
    },

    accessLevelInput: {
      get() {
        return this.accessLevel;
      },
      set(value) {
        this.$store.commit("documents/UPDATE_ACCESS_LEVEL", value);
      },
    },

    downloadsColumns() {
      return [
        { field: "document_number", header: "Download No.", sortable: true, custom: false, width: "15%" },
        { field: "name", header: "Name", sortable: true, custom: false, width: "35%" },
        { field: "category", header: "Category", sortable: true, custom: false, width: "15%" },
        { field: "visibility", header: "Visibility", sortable: true, custom: false, width: "10%" },
        { field: "access_level", header: "Access", sortable: true, custom: false, width: "10%" },
        { field: "created_at", header: "Created", sortable: true, custom: true, slotName: "created-at-slot", width: "10%" },
        { field: "action", header: "", sortable: false, custom: true, slotName: "action-slot", width: "5%" },
      ];
    },

    bulletinsColumns() {
      return [
        { field: "document_number", header: "Bulletin No.", sortable: true, custom: false, width: "15%" },
        { field: "name", header: "Name", sortable: true, custom: false, width: "35%" },
        { field: "category", header: "Category", sortable: true, custom: false, width: "15%" },
        { field: "visibility", header: "Visibility", sortable: true, custom: false, width: "10%" },
        { field: "document_date", header: "Doc Date", sortable: true, custom: true, slotName: "doc-date-slot", width: "10%" },
        { field: "created_at", header: "Created", sortable: true, custom: true, slotName: "created-at-slot", width: "10%" },
        { field: "action", header: "", sortable: false, custom: true, slotName: "action-slot", width: "5%" },
      ];
    },

    allColumns() {
      return [
        { field: "document_type", header: "Type", sortable: true, custom: false, width: "10%" },
        { field: "document_number", header: "Number", sortable: true, custom: false, width: "15%" },
        { field: "name", header: "Name", sortable: true, custom: false, width: "30%" },
        { field: "category", header: "Category", sortable: true, custom: false, width: "15%" },
        { field: "visibility", header: "Visibility", sortable: true, custom: false, width: "10%" },
        { field: "document_date", header: "Doc Date", sortable: true, custom: true, slotName: "doc-date-slot", width: "10%" },
        { field: "created_at", header: "Created", sortable: true, custom: true, slotName: "created-at-slot", width: "10%" },
        { field: "action", header: "", sortable: false, custom: true, slotName: "action-slot", width: "5%" },
      ];
    },
  },

  mounted() {
    this.fetchTableData();
  },

  methods: {
    ...mapActions("documents", ["fetchTableData", "updateTab", "resetCurrentPage"]),

    onTabSelected(newTab) {
      this.updateTab(newTab);
    },

    async viewDoc(rowData) {
      try {
        const doc = await this.DocumentService.getDocumentById(rowData.document_id);
        this.viewingDocument = {
          doc: doc,
          panelOpen: true,
        };
      } catch (error) {
        console.error(error);
      }
    },

    validateDates() {
      const hasFromDate = !!this.fromDateInput;
      const hasToDate = !!this.toDateInput;

      if (hasFromDate && hasToDate) {
        if (this.toDateInput < this.fromDateInput) {
          return false;
        }
        return true;
      }

      if (hasFromDate !== hasToDate) {
        return false;
      }

      return true;
    },

    triggerSearch() {
      if (this.validateDates()) {
        this.resetCurrentPage(0);
        this.updateTab(this.currentTab);
      }
    },

    onRowClick(event) {
      this.$store.commit("documents/SET_LAST_SELECTED_ROW", event.data.document_id);
    },

    formatTimestamp(unixTimestamp) {
      if (!unixTimestamp) {
        return "";
      }
      return this.$moment.unix(unixTimestamp).format("DD-MM-YYYY");
    },

    async downloadDoc(rowData) {
      try {
        const doc = await this.DocumentService.getDocumentById(rowData.document_id);
        window.open(doc.access_url);
      } catch (error) {
        console.error(error);
      }
    },

    editDoc(rowData) {
      this.editingDoc = rowData;
      this.showingUploadDocumentPanel = true;
    },

    deleteDoc(rowData) {
      this.confirmAlert = {
        resultFunction: this.handleDeleteDoc,
        message: "Are you sure you want to delete this document? Cannot be undone",
        showing: true,
        data: rowData,
      };
    },

    async handleDeleteDoc(result) {
      if (result) {
        try {
          await this.DocumentService.deleteDocument(this.confirmAlert.data.document_id);
          await this.fetchTableData();
        } catch (error) {
          console.error(error);
        }
      }

      this.confirmAlert = {
        resultFunction: "",
        message: "",
        showing: false,
        data: "",
      };
    },

    closePanel() {
      this.editingDoc = {
        visibility: "Internal",
      };
      this.showingUploadDocumentPanel = false;
    },

    async onComplete() {
      this.closePanel();
      await this.fetchTableData();
    },
  },
};
</script>
